<!--
  SidebarNavItem.vue

  Extracted from DistributorLayout.vue (PR #1403 review P2-2, card
  q2mwfFVR). The distributor sidebar had 9 nearly-identical <li> blocks --
  each wiring a router-link, an icon, a label, an optional sub-text row,
  an optional status dot, and (Payouts only) an inline badge. Rule-of-two
  hit; consolidate into one component so future tweaks are one-file
  changes.

  The CSS for the rendered structure lives here in scoped styles. The
  parent layout still owns the .dist-sidebar / section / ul chrome and
  visibility gating (Owner vs. Builder capability checks).

  Active-link styling: the <router-link> at the root gets the
  router-link-active class via Vue Router. The .dist-sidebar__item class
  on that link drives the highlighted background + colored left border.

  Sub-text precedence: staticSubText wins over summarySlot.sub_text when
  both are present. Performance is the one row that uses staticSubText
  (its descriptor is hardcoded -- "GMV, top products, trends" -- and
  doesn't depend on a backend summary slot).
-->
<template>
    <router-link :to="to" class="dist-sidebar__item">
        <span class="dist-sidebar__row">
            <v-icon
                class="dist-sidebar__icon"
                :icon="icon"
                size="18"
            />
            <span class="dist-sidebar__label">{{ label }}</span>
            <span
                v-if="badgeText"
                class="dist-sidebar__badge"
                :data-testid="`dist-sidebar-badge-${keyName}`"
                >{{ badgeText }}</span
            >
            <span
                v-if="dotStatus"
                class="dist-sidebar__status-dot"
                :class="statusDotClass"
                :data-status="dotStatus"
                :data-testid="`dist-sidebar-dot-${keyName}`"
            ></span>
        </span>
        <span
            v-if="effectiveSubText"
            class="dist-sidebar__subtext"
            :data-testid="`dist-sidebar-subtext-${keyName}`"
            >{{ effectiveSubText }}</span
        >
    </router-link>
</template>

<script>
export default {
    name: "SidebarNavItem",
    props: {
        to: {
            type: String,
            required: true,
        },
        icon: {
            type: String,
            required: true,
        },
        label: {
            type: String,
            required: true,
        },
        keyName: {
            // Mints deterministic data-testids for behavioral tests:
            // dist-sidebar-{subtext,dot,badge}-{keyName}. Use the
            // summary-key name (stores, decoration_library, etc.) so the
            // test identifier matches the backend key.
            type: String,
            required: true,
        },
        summarySlot: {
            // { status, sub_text, badge } or null. The summary store may
            // return null for the entire row (e.g. true SuperAdmin with
            // no distributor context) or for individual fields.
            type: Object,
            default: null,
        },
        staticSubText: {
            // Override for rows that hardcode their descriptor
            // (Performance). Wins over summarySlot.sub_text when set.
            type: String,
            default: "",
        },
    },
    computed: {
        effectiveSubText() {
            // staticSubText wins; otherwise fall back to the summary slot.
            if (this.staticSubText) return this.staticSubText;
            return this.summarySlot?.sub_text || "";
        },
        dotStatus() {
            // Only return a status if it's one we know how to color.
            const s = this.summarySlot?.status;
            if (s === "green" || s === "amber" || s === "red") return s;
            return null;
        },
        statusDotClass() {
            // ``dotStatus`` is already guarded to one of the three known
            // colors (or null) by the computed above, so the template
            // contract is "if a status is here, it's a known color." A
            // single interpolation is enough; no need to re-branch.
            return this.dotStatus
                ? `dist-sidebar__status-dot--${this.dotStatus}`
                : "";
        },
        badgeText() {
            return this.summarySlot?.badge || "";
        },
    },
};
</script>

<style scoped>
.dist-sidebar__item {
    /*
     * Two-row layout: label row (icon + label + optional badge + status
     * dot) on top, sub-text row below. Flex column lets the sub-text
     * sit under the label.
     */
    display: flex;
    flex-direction: column;
    gap: 0;
    padding: 6px 16px 6px 16px;
    color: #2d3748;
    text-decoration: none;
    font-size: 13px;
    line-height: 1.4;
    border-left: 3px solid transparent;
}
.dist-sidebar__row {
    /*
     * Inner label row -- icon + label + badge + status dot all sit here.
     * The status dot uses margin-left: auto to anchor to the right edge.
     */
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}
.dist-sidebar__item:hover {
    background: #edf2f7;
}
.dist-sidebar__item.router-link-active {
    background: var(--tf-primary-soft);
    color: var(--tf-primary-press);
    border-left-color: var(--tf-primary);
    font-weight: 500;
}
.dist-sidebar__icon {
    flex-shrink: 0;
    color: var(--tf-fg-4);
}
.dist-sidebar__item.router-link-active .dist-sidebar__icon {
    color: var(--tf-primary);
}
.dist-sidebar__label {
    flex: 1;
    min-width: 0;
}
.dist-sidebar__subtext {
    /*
     * Per-row sub-text -- e.g. "6 stores - 4 live". Indented to align
     * with the label text (icon width 18 + row gap 10 = 28px from the
     * row left edge). Color matches the topbar muted color used for icons.
     */
    display: block;
    font-size: 13px;
    color: #374151;
    margin-top: 2px;
    padding-left: 28px;
    line-height: 1.3;
}
.dist-sidebar__item.router-link-active .dist-sidebar__subtext {
    /*
     * The muted gray looks washed out on the active blue background,
     * so re-assert a softer active tone here.
     */
    color: #0a4f78;
}
.dist-sidebar__status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-left: auto;
    flex-shrink: 0;
}
.dist-sidebar__status-dot--green {
    background: var(--tf-success);
}
.dist-sidebar__status-dot--amber {
    background: #f0a04b;
}
.dist-sidebar__status-dot--red {
    background: var(--tf-red);
}
.dist-sidebar__badge {
    /*
     * Small pill rendered inline with the label -- currently used for
     * "Soon" on Payouts. Sits between the label and the optional status
     * dot. Not margin-left: auto so it hugs the label.
     */
    display: inline-block;
    margin-left: 6px;
    padding: 1px 7px;
    border-radius: 9999px;
    font-size: 10px;
    font-weight: 500;
    background: #edf2f7;
    color: #4a5568;
}
</style>
